import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { FaTimes, FaStar, FaRegStar, FaChevronDown, FaChevronRight, FaBuilding } from 'react-icons/fa';
import api from '../../services/api';

interface PerguntaResultado {
  idPergunta: string;
  texto: string;
  tipoPergunta: number;
  respostas: string[];
  contagem: Record<string, number>;
  media: number | null;
}

interface RespostaItemDetalhado {
  idPergunta: string;
  textoPergunta: string;
  tipoPergunta: number;
  valor: string;
}

interface RespostaDetalhada {
  idResposta: string;
  idEmpresa: string;
  nomeEmpresa: string;
  dataResposta: string;
  itens: RespostaItemDetalhado[];
}

interface ResultadoDto {
  idPesquisa: string;
  titulo: string;
  totalRespostas: number;
  perguntas: PerguntaResultado[];
  respostasDetalhadas: RespostaDetalhada[];
}

interface Props {
  pesquisaId: string;
  onClose: () => void;
}

const tipoLabel: Record<number, string> = { 0: 'Texto', 1: 'Nota', 2: 'Multipla Escolha', 3: 'Sim/Nao', 4: 'NPS' };

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex; align-items: flex-start; justify-content: center;
  z-index: 1050; padding: 40px 20px;
  overflow-y: auto;
  animation: ${fadeIn} 0.15s ease-out;
`;

const Modal = styled.div`
  background: #fff; border-radius: 16px;
  width: 100%; max-width: 800px;
  box-shadow: 0 25px 60px rgba(0,0,0,0.2);
  animation: ${slideUp} 0.25s ease-out;
`;

const ModalHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 24px 28px; border-bottom: 1px solid #f1f5f9;
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem; font-weight: 700; color: #0f172a; margin: 0;
`;

const CloseBtn = styled.button`
  border: none; background: none; cursor: pointer; color: #94a3b8;
  font-size: 1.2rem; padding: 4px;
  &:hover { color: #475569; }
`;

const ModalBody = styled.div`
  padding: 24px 28px; max-height: 70vh; overflow-y: auto;
`;

const StatCards = styled.div`
  display: flex; gap: 16px; margin-bottom: 24px; flex-wrap: wrap;
`;

const StatCard = styled.div`
  flex: 1; min-width: 120px;
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  padding: 16px; border-radius: 12px; text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.8rem; font-weight: 800; color: #1e40af;
`;

const StatLabel = styled.div`
  font-size: 0.78rem; color: #64748b; font-weight: 500; margin-top: 4px;
`;

const PerguntaSection = styled.div`
  margin-bottom: 24px; background: #f8fafc;
  border: 1px solid #e2e8f0; border-radius: 12px;
  overflow: hidden;
`;

const PerguntaSectionHeader = styled.div`
  padding: 16px 20px; background: #fff;
  border-bottom: 1px solid #e2e8f0;
  display: flex; justify-content: space-between; align-items: center;
`;

const PerguntaTexto = styled.h4`
  font-size: 0.95rem; font-weight: 600; color: #0f172a; margin: 0;
`;

const TipoBadge = styled.span`
  font-size: 0.72rem; font-weight: 600; color: #6366f1;
  background: #eef2ff; padding: 3px 10px; border-radius: 20px;
`;

const PerguntaBody = styled.div`
  padding: 16px 20px;
`;

const BarChart = styled.div`
  display: flex; flex-direction: column; gap: 8px;
`;

const BarRow = styled.div`
  display: flex; align-items: center; gap: 12px;
`;

const BarLabel = styled.span`
  font-size: 0.82rem; color: #374151; min-width: 100px;
  font-weight: 500; text-align: right;
`;

const BarTrack = styled.div`
  flex: 1; height: 28px; background: #e2e8f0;
  border-radius: 6px; overflow: hidden; position: relative;
`;

const BarFill = styled.div<{ width: number; color: string }>`
  height: 100%;
  width: ${p => p.width}%;
  background: ${p => p.color};
  border-radius: 6px;
  transition: width 0.5s ease;
  display: flex; align-items: center;
  padding: 0 8px; min-width: fit-content;
`;

const BarValue = styled.span`
  font-size: 0.75rem; font-weight: 600; color: #fff;
  white-space: nowrap;
`;

const BarCount = styled.span`
  font-size: 0.8rem; color: #64748b; min-width: 40px;
`;

const MediaDisplay = styled.div`
  text-align: center; padding: 12px;
`;

const MediaValue = styled.div`
  font-size: 2.2rem; font-weight: 800; color: #f59e0b;
  display: flex; align-items: center; justify-content: center; gap: 8px;
`;

const RespostaTextoList = styled.div`
  display: flex; flex-direction: column; gap: 6px;
`;

const RespostaTextoItem = styled.div`
  background: #fff; padding: 10px 14px;
  border-radius: 8px; font-size: 0.85rem;
  color: #374151; border: 1px solid #e2e8f0;
  line-height: 1.4;
`;

const EmptyResult = styled.div`
  text-align: center; color: #94a3b8; padding: 20px;
  font-size: 0.9rem;
`;

const DetalhamentoTitulo = styled.h3`
  font-size: 1rem; font-weight: 700; color: #0f172a;
  margin: 32px 0 12px 0; display: flex; align-items: center; gap: 8px;
`;

const RespostaCard = styled.div`
  background: #fff; border: 1px solid #e2e8f0;
  border-radius: 10px; margin-bottom: 10px; overflow: hidden;
`;

const RespostaHeader = styled.button`
  width: 100%; background: #f8fafc; border: none;
  padding: 12px 16px; display: flex; align-items: center; gap: 12px;
  cursor: pointer; text-align: left;
  &:hover { background: #f1f5f9; }
`;

const RespostaInfo = styled.div`
  flex: 1; display: flex; flex-direction: column; gap: 2px;
`;

const RespostaEmpresa = styled.span`
  font-size: 0.9rem; font-weight: 600; color: #0f172a;
  display: flex; align-items: center; gap: 6px;
`;

const RespostaData = styled.span`
  font-size: 0.75rem; color: #64748b;
`;

const RespostaCorpo = styled.div`
  padding: 12px 16px; background: #fff;
  border-top: 1px solid #e2e8f0;
  display: flex; flex-direction: column; gap: 10px;
`;

const ItemDetalhe = styled.div`
  display: flex; flex-direction: column; gap: 3px;
  padding: 8px 0; border-bottom: 1px dashed #e2e8f0;
  &:last-child { border-bottom: none; }
`;

const ItemPergunta = styled.span`
  font-size: 0.78rem; color: #64748b; font-weight: 500;
`;

const ItemValor = styled.span`
  font-size: 0.88rem; color: #0f172a; font-weight: 600;
  word-break: break-word;
`;

const ChevronIcon = styled.span`
  color: #64748b; font-size: 0.75rem;
`;

const npsColors = (score: number): string => {
  if (score <= 6) return '#ef4444';
  if (score <= 8) return '#f59e0b';
  return '#22c55e';
};

const barColors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#22c55e', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6'];

const formatarDataResposta = (dataIso: string): string => {
  const data = new Date(dataIso);
  if (isNaN(data.getTime())) return '';
  return data.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatarValorItem = (tipoPergunta: number, valor: string): string => {
  if (!valor) return '-';
  if (tipoPergunta === 1) return `${valor} estrela${Number(valor) > 1 ? 's' : ''}`;
  if (tipoPergunta === 4) return `${valor}/10`;
  return valor;
};

const PesquisaResultadosModal: React.FC<Props> = ({ pesquisaId, onClose }) => {
  const [resultado, setResultado] = useState<ResultadoDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandidas, setExpandidas] = useState<Record<string, boolean>>({});

  const toggleResposta = (id: string) => {
    setExpandidas(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const carregar = async () => {
      try {
        const res = await api.get(`/PesquisaResultados/${pesquisaId}`);
        setResultado(res.data);
      } catch {
        toast.error('Erro ao carregar resultados');
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [pesquisaId]);

  const renderPergunta = (p: PerguntaResultado) => {
    if (p.respostas.length === 0) return <EmptyResult>Nenhuma resposta</EmptyResult>;

    const total = p.respostas.length;

    if (p.tipoPergunta === 0) {
      return (
        <RespostaTextoList>
          {p.respostas.map((r, i) => <RespostaTextoItem key={i}>{r}</RespostaTextoItem>)}
        </RespostaTextoList>
      );
    }

    if (p.tipoPergunta === 1) {
      return (
        <>
          <MediaDisplay>
            <MediaValue>
              {[1, 2, 3, 4, 5].map(s => s <= Math.round(p.media || 0) ? <FaStar key={s} /> : <FaRegStar key={s} />)}
              <span style={{ marginLeft: 8 }}>{p.media?.toFixed(1)}</span>
            </MediaValue>
            <StatLabel>{total} avaliacao(oes)</StatLabel>
          </MediaDisplay>
          <BarChart>
            {[5, 4, 3, 2, 1].map(nota => {
              const count = p.contagem[String(nota)] || 0;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <BarRow key={nota}>
                  <BarLabel>{nota} estrela{nota > 1 ? 's' : ''}</BarLabel>
                  <BarTrack>
                    <BarFill width={pct} color="#f59e0b">
                      {pct > 10 && <BarValue>{Math.round(pct)}%</BarValue>}
                    </BarFill>
                  </BarTrack>
                  <BarCount>{count}</BarCount>
                </BarRow>
              );
            })}
          </BarChart>
        </>
      );
    }

    if (p.tipoPergunta === 4) {
      const nps = p.media || 0;
      const promoters = Object.entries(p.contagem).filter(([k]) => Number(k) >= 9).reduce((s, [, v]) => s + v, 0);
      const detractors = Object.entries(p.contagem).filter(([k]) => Number(k) <= 6).reduce((s, [, v]) => s + v, 0);
      const npsScore = total > 0 ? Math.round(((promoters - detractors) / total) * 100) : 0;

      return (
        <>
          <MediaDisplay>
            <MediaValue style={{ color: npsColors(nps) }}>NPS: {npsScore}</MediaValue>
            <StatLabel>Media: {nps.toFixed(1)} | {total} respostas</StatLabel>
          </MediaDisplay>
          <BarChart>
            {Array.from({ length: 11 }, (_, i) => i).reverse().map(nota => {
              const count = p.contagem[String(nota)] || 0;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <BarRow key={nota}>
                  <BarLabel>{nota}</BarLabel>
                  <BarTrack>
                    <BarFill width={pct} color={nota <= 6 ? '#ef4444' : nota <= 8 ? '#f59e0b' : '#22c55e'}>
                      {pct > 8 && <BarValue>{Math.round(pct)}%</BarValue>}
                    </BarFill>
                  </BarTrack>
                  <BarCount>{count}</BarCount>
                </BarRow>
              );
            })}
          </BarChart>
        </>
      );
    }

    // Multipla escolha e Sim/Nao
    const entries = Object.entries(p.contagem).sort((a, b) => b[1] - a[1]);
    return (
      <BarChart>
        {entries.map(([label, count], i) => {
          const pct = total > 0 ? (count / total) * 100 : 0;
          return (
            <BarRow key={label}>
              <BarLabel>{label}</BarLabel>
              <BarTrack>
                <BarFill width={pct} color={barColors[i % barColors.length]}>
                  {pct > 10 && <BarValue>{Math.round(pct)}%</BarValue>}
                </BarFill>
              </BarTrack>
              <BarCount>{count}</BarCount>
            </BarRow>
          );
        })}
      </BarChart>
    );
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Resultados{resultado ? `: ${resultado.titulo}` : ''}</ModalTitle>
          <CloseBtn onClick={onClose}><FaTimes /></CloseBtn>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <EmptyResult>Carregando resultados...</EmptyResult>
          ) : !resultado ? (
            <EmptyResult>Erro ao carregar</EmptyResult>
          ) : (
            <>
              <StatCards>
                <StatCard>
                  <StatValue>{resultado.totalRespostas}</StatValue>
                  <StatLabel>Total de Respostas</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{resultado.perguntas.length}</StatValue>
                  <StatLabel>Perguntas</StatLabel>
                </StatCard>
              </StatCards>

              {resultado.perguntas.map(p => (
                <PerguntaSection key={p.idPergunta}>
                  <PerguntaSectionHeader>
                    <PerguntaTexto>{p.texto}</PerguntaTexto>
                    <TipoBadge>{tipoLabel[p.tipoPergunta]}</TipoBadge>
                  </PerguntaSectionHeader>
                  <PerguntaBody>{renderPergunta(p)}</PerguntaBody>
                </PerguntaSection>
              ))}

              {resultado.respostasDetalhadas && resultado.respostasDetalhadas.length > 0 && (
                <>
                  <DetalhamentoTitulo>
                    <FaBuilding /> Respostas Individuais ({resultado.respostasDetalhadas.length})
                  </DetalhamentoTitulo>
                  {resultado.respostasDetalhadas.map(r => {
                    const aberta = !!expandidas[r.idResposta];
                    return (
                      <RespostaCard key={r.idResposta}>
                        <RespostaHeader onClick={() => toggleResposta(r.idResposta)}>
                          <ChevronIcon>{aberta ? <FaChevronDown /> : <FaChevronRight />}</ChevronIcon>
                          <RespostaInfo>
                            <RespostaEmpresa>{r.nomeEmpresa}</RespostaEmpresa>
                            <RespostaData>{formatarDataResposta(r.dataResposta)}</RespostaData>
                          </RespostaInfo>
                        </RespostaHeader>
                        {aberta && (
                          <RespostaCorpo>
                            {r.itens.map(item => (
                              <ItemDetalhe key={item.idPergunta}>
                                <ItemPergunta>{item.textoPergunta}</ItemPergunta>
                                <ItemValor>{formatarValorItem(item.tipoPergunta, item.valor)}</ItemValor>
                              </ItemDetalhe>
                            ))}
                          </RespostaCorpo>
                        )}
                      </RespostaCard>
                    );
                  })}
                </>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default PesquisaResultadosModal;
